// @ts-nocheck
// ============================================
// useNavigationState - хук для автоматического восстановления состояния навигации
// ============================================

import { useEffect, useCallback, useRef } from 'react';
import { useStore } from '../store';
import {
  getNavigationState,
  saveAppId,
  saveVersionId,
  saveScreenId,
  findValidVersion,
  validateSavedAppExists,
} from '../utils/navigationState';

/**
 * Хук для автоматического восстановления состояния навигации после загрузки приложений
 */
export function useNavigationState() {
  const apps = useStore(state => state.apps);
  const currentApp = useStore(state => state.currentApp);
  const currentVersion = useStore(state => state.currentVersion);
  const currentScreen = useStore(state => state.currentScreen);
  
  const setCurrentApp = useStore(state => state.setCurrentApp);
  const setCurrentVersion = useStore(state => state.setCurrentVersion);
  const setCurrentScreen = useStore(state => state.setCurrentScreen);
  
  const hasRestored = useRef(false);
  const isRestoring = useRef(false);
  
  /**
   * Восстановить состояние навигации из localStorage
   */
  const restoreNavigationState = useCallback(() => {
    if (isRestoring.current || hasRestored.current) {
      console.log('[useNavigationState] Already restored or restoring, skipping');
      return;
    }
    
    if (!apps.length) {
      console.log('[useNavigationState] No apps loaded yet, skipping');
      return;
    }
    
    isRestoring.current = true;
    
    const { currentAppId, currentVersionId, currentScreenId } = getNavigationState();
    
    console.log('[useNavigationState] Attempting to restore:', {
      currentAppId,
      currentVersionId,
      currentScreenId,
      currentApp: currentApp?.id,
      appsCount: apps.length
    });
    
    // Проверяем, нужно ли восстанавливать
    // Восстанавливаем если:
    // 1. Есть сохранённый appId И
    // 2. Нет текущего выбранного приложения ИЛИ текущее приложение не совпадает с сохранённым
    const needsRestore = currentAppId && (!currentApp || currentApp.id !== currentAppId);
    
    if (!needsRestore) {
      console.log('[useNavigationState] No restore needed');
      isRestoring.current = false;
      return;
    }
    
    // Проверяем, существует ли сохранённое приложение
    const { isValid, appExists } = validateSavedAppExists(currentAppId, apps);
    
    if (!isValid) {
      console.log('[useNavigationState] Saved app not found in apps list, clearing state');
      // Сохранённое приложение не существует, очищаем localStorage
      if (currentAppId) {
        // Не очищаем, просто выбираем первое доступное
      }
      isRestoring.current = false;
      return;
    }
    
    console.log('[useNavigationState] ✅ Restoring navigation state for app:', currentAppId);
    
    // Находим приложение
    const app = apps.find(a => a.id === currentAppId);
    
    if (!app) {
      console.log('[useNavigationState] App not found, clearing');
      isRestoring.current = false;
      return;
    }
    
    // Находим версию
    const { version, screenId } = findValidVersion(app, currentVersionId);
    
    if (!version) {
      console.log('[useNavigationState] No version found, clearing');
      isRestoring.current = false;
      return;
    }
    
    console.log('[useNavigationState] Restoring:', {
      appId: app.id,
      versionId: version.id,
      screenId: screenId || currentScreenId
    });
    
    // Восстанавливаем приложение
    setCurrentApp(app.id);
    
    // После небольшой задержки восстанавливаем версию
    setTimeout(() => {
      setCurrentVersion(version.id);
      
      // После ещё одной задержки восстанавливаем экран
      setTimeout(() => {
        const finalScreenId = currentScreenId && version.screens?.some((s: any) => s.id === currentScreenId)
          ? currentScreenId
          : screenId;
        
        if (finalScreenId) {
          setCurrentScreen(finalScreenId);
        }
        
        hasRestored.current = true;
        isRestoring.current = false;
        console.log('[useNavigationState] ✅ Navigation state restored successfully');
      }, 50);
    }, 50);
  }, [apps, currentApp, setCurrentApp, setCurrentVersion, setCurrentScreen]);
  
  /**
   * Эффект для автоматического восстановления при загрузке приложений
   */
  useEffect(() => {
    // Запускаем восстановление когда приложения загружены
    if (apps.length > 0 && !hasRestored.current) {
      console.log('[useNavigationState] Apps loaded, attempting restore');
      restoreNavigationState();
    }
  }, [apps.length, restoreNavigationState]);
  
  /**
   * Следим за изменением текущего приложения и сохраняем в localStorage
   */
  useEffect(() => {
    if (currentApp?.id) {
      saveAppId(currentApp.id);
    }
  }, [currentApp?.id]);
  
  /**
   * Следим за изменением текущей версии и сохраняем в localStorage
   */
  useEffect(() => {
    if (currentVersion?.id) {
      saveVersionId(currentVersion.id);
    }
  }, [currentVersion?.id]);
  
  /**
   * Следим за изменением текущего экрана и сохраняем в localStorage
   */
  useEffect(() => {
    if (currentScreen?.id) {
      saveScreenId(currentScreen.id);
    }
  }, [currentScreen?.id]);
  
  return {
    restoreNavigationState,
    hasRestored: hasRestored.current,
  };
}

/**
 * Хук для принудительного сброса состояния навигации
 */
export function useResetNavigationState() {
  const setCurrentApp = useStore(state => state.setCurrentApp);
  const setCurrentVersion = useStore(state => state.setCurrentVersion);
  const setCurrentScreen = useStore(state => state.setCurrentScreen);
  
  const resetNavigationState = useCallback(() => {
    console.log('[useResetNavigationState] Resetting navigation state');
    
    // Очищаем store
    setCurrentApp('');
    setCurrentVersion('');
    setCurrentScreen('');
    
    // Очищаем localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('currentAppId');
      localStorage.removeItem('currentVersionId');
      localStorage.removeItem('currentScreenId');
    }
  }, [setCurrentApp, setCurrentVersion, setCurrentScreen]);
  
  return { resetNavigationState };
}
